import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "../lib/apiClient";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Film, File as FileIcon, Download, Trash2, Eye } from "lucide-react";

const MAX_BYTES = 100 * 1024 * 1024;

function humanSize(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}

function kind(ct = "") {
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "video";
  if (ct === "application/pdf") return "pdf";
  return "file";
}

function TypeIcon({ ct }) {
  const k = kind(ct);
  if (k === "image") return <ImageIcon className="w-4 h-4 text-sky-600" />;
  if (k === "video") return <Film className="w-4 h-4 text-purple-600" />;
  if (k === "pdf") return <FileText className="w-4 h-4 text-red-600" />;
  return <FileIcon className="w-4 h-4 text-muted-foreground" />;
}

export function GrievanceAttachments({ gid, canEdit }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const token = localStorage.getItem("gt_token");

  const fileUrl = (fid, dl = false) => `${API}/grievances/${gid}/files/${fid}?auth=${token}${dl ? "&download=1" : ""}`;

  const load = useCallback(() => {
    api.get(`/grievances/${gid}/files`).then((r) => setFiles(r.data)).catch(() => {});
  }, [gid]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setUploading(true);
    for (const f of picked) {
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" exceeds the 100 MB limit`);
        continue;
      }
      const form = new FormData();
      form.append("file", f);
      try {
        setProgress(0);
        await api.post(`/grievances/${gid}/files`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || ev.loaded)) * 100)),
        });
        toast.success(`Uploaded ${f.name}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    setProgress(0);
    load();
  };

  const remove = async (fid, name) => {
    try {
      await api.delete(`/grievances/${gid}/files/${fid}`);
      toast.success(`Removed ${name}`);
      load();
    } catch (e) {
      toast.error("Failed to remove file");
    }
  };

  return (
    <div className="border border-border bg-card rounded-sm" data-testid="attachments-panel">
      <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Attachments</h2>
        {canEdit && (
          <>
            <input ref={inputRef} type="file" multiple hidden onChange={onPick}
              accept="application/pdf,image/*,video/*" data-testid="attachment-file-input" />
            <Button size="sm" variant="outline" className="rounded-sm h-8" disabled={uploading}
              data-testid="attachment-upload-btn" onClick={() => inputRef.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {uploading ? `Uploading… ${progress}%` : "Upload files"}
            </Button>
          </>
        )}
      </div>
      <div className="p-5">
        {files.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No files attached yet.{canEdit ? " Upload PDFs, images or videos above." : ""}
          </div>
        ) : (
          <div className="space-y-3">
            {files.map((f) => (
              <div key={f.id} data-testid={`attachment-${f.id}`}
                className="flex items-center gap-3 border border-border rounded-sm p-2.5">
                {kind(f.content_type) === "image" ? (
                  <img src={fileUrl(f.id)} alt={f.original_filename}
                    className="w-12 h-12 object-cover rounded-sm border border-border shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-sm bg-secondary flex items-center justify-center shrink-0">
                    <TypeIcon ct={f.content_type} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{f.original_filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {humanSize(f.size)}{f.uploaded_by ? ` · ${f.uploaded_by}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={fileUrl(f.id)} target="_blank" rel="noreferrer"
                    data-testid={`attachment-view-${f.id}`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-secondary text-muted-foreground" title="View">
                    <Eye className="w-4 h-4" />
                  </a>
                  <a href={fileUrl(f.id, true)}
                    data-testid={`attachment-download-${f.id}`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-secondary text-muted-foreground" title="Download">
                    <Download className="w-4 h-4" />
                  </a>
                  {canEdit && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`attachment-delete-${f.id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-secondary text-destructive" title="Remove">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm bg-card">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove “{f.original_filename}”?</AlertDialogTitle>
                          <AlertDialogDescription>This file will be removed from the grievance.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                          <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90"
                            onClick={() => remove(f.id, f.original_filename)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
